import type { WorkspaceNode, WorkspaceNodeType } from "../../../types/workspace";
import { isPersistedWorkspaceResourceId } from "../../../utils/workspacePersistIds";

const DELETABLE_ENTITY_TYPES = new Set<WorkspaceNodeType>([
  "strategy",
  "report",
  "simulation",
  "combined_simulation",
  "policy",
  "combined_policy",
]);

const NODE_TYPE_LABEL: Record<WorkspaceNodeType, string> = {
  account: "Account",
  strategy: "Strategy",
  report: "Report",
  simulation: "Simulation",
  policy: "Policy",
  combined_policy: "Combined policy",
  combined_simulation: "Combined simulation",
};

/** True when the canvas Remove… dialog may offer permanent entity delete. */
export function workspaceNodeCanDeleteEntity(node: WorkspaceNode): boolean {
  if (node.type === "account") return false;
  if (!DELETABLE_ENTITY_TYPES.has(node.type)) return false;
  return isPersistedWorkspaceResourceId(node.entityId);
}

/** Confirmation body for permanent delete of the underlying entity. */
export function describeWorkspaceEntityDelete(
  node: WorkspaceNode,
  nodeTitle: string
): { title: string; body: string; confirmLabel: string } {
  const typeLabel = NODE_TYPE_LABEL[node.type];
  const titleText = nodeTitle.trim() || typeLabel;

  switch (node.type) {
    case "strategy":
      return {
        title: "Delete strategy?",
        body: `Delete “${titleText}” permanently? All policy versions will be removed. Existing reports will stay but will no longer be linked to this strategy. This cannot be undone.`,
        confirmLabel: "Delete strategy",
      };
    case "report":
      return {
        title: "Delete report?",
        body: `Delete “${titleText}” permanently? This cannot be undone.`,
        confirmLabel: "Delete report",
      };
    case "simulation":
    case "combined_simulation":
      return {
        title: "Delete simulation?",
        body: `Delete “${titleText}” permanently? This cannot be undone.`,
        confirmLabel: "Delete simulation",
      };
    case "policy":
    case "combined_policy":
      return {
        title: "Delete policy version?",
        body: `Delete “${titleText}” permanently? This cannot be undone.`,
        confirmLabel: "Delete policy version",
      };
    default:
      return {
        title: "Delete?",
        body: `Delete “${titleText}” permanently? This cannot be undone.`,
        confirmLabel: "Delete",
      };
  }
}
